class FilterService {
    static OPERATORS = {
        EQUAL: '=',
        GREATER: '>',
        LESS: '<',
        LIKE: ':',
        NOT_EQUAL: '!=',
        IN_LIST: '@',
        NOT_IN_LIST: '!@',
    };

    constructor() {
        this.groups = {}; // Record<string, Filter[][]>
        this.meta = {}; // Record<string, { internal: boolean }>
    }

    _getGroupName(groupName) {
        return groupName || 'default';
    }

    markInternal(groupName, isInternal = true) {
        const name = this._getGroupName(groupName);
        if (!this.meta[name]) this.meta[name] = {};
        this.meta[name].internal = !!isInternal;
        return this;
    }

    _isInternal(name) {
        return !!(this.meta[name] && this.meta[name].internal);
    }

    /**
     * Adds a new filter to the specified group
     * @param {string} field - The field name
     * @param {string} op - The operator
     * @param {*} value - The filter value
     * @param {string|null} groupName - The group name (defaults to 'default' if null)
     * @param {boolean} markInternal - Whether to mark this filter as internal
     * @returns {FilterService} The current FilterService instance for chaining
     */
    addFilter(field, op, value, groupName = null, markInternal = false) {
        const name = this._getGroupName(groupName);
        if (!this.groups[name]) this.groups[name] = [];

        const newFilter = { field, op, value };

        this._replaceOrPushFilter(name, [newFilter]);
        if (markInternal) this.markInternal(name, true);
        return this;
    }

    /**
     * Adds a group of filters (with AND between them)
     * @param {Array} groupArray - Array of filter objects
     * @param {string|null} groupName - The group name (defaults to 'default' if null)
     * @returns {FilterService} The current FilterService instance for chaining
     */
    addGroup(groupArray, groupName = null) {
        const name = this._getGroupName(groupName);
        this.groups[name] = [];

        const group = groupArray.map(({ field, op, value }) => ({
            field,
            op,
            value,
        }));

        this.groups[name].push(group);
        return this;
    }

    /**
     * Adds multiple filter groups (each group has AND relationship between filters)
     * @param {Array} groupsArray - Array of filter groups
     * @param {string|null} groupName - The group name (defaults to 'default' if null)
     * @returns {FilterService} The current FilterService instance for chaining
     */
    addGroups(groupsArray, groupName = null) {
        const name = this._getGroupName(groupName);
        this.groups[name] = [];

        const processed = groupsArray.map(group =>
            group.map(({ field, op, value }) => ({
                field,
                op,
                value,
            }))
        );

        this.groups[name].push(...processed);
        return this;
    }

    _replaceOrPushFilter(groupName, newGroup) {
        const [newFilter] = newGroup;
        const id = newFilter.id;

        // Check if the filter already exists
        for (let i = 0; i < this.groups[groupName].length; i++) {
            const group = this.groups[groupName][i];
            for (let j = 0; j < group.length; j++) {
                if (group[j].id === id) {
                    this.groups[groupName][i][j] = newFilter;
                    return;
                }
            }
        }

        // If it doesn't exist, push the new filter
        this.groups[groupName].push(newGroup);
    }


    getFilters() {
        const filters = [];
        for (const name in this.groups) {
            if (!this._isInternal(name)) {
                filters.push(...this.groups[name]);
            }
        }
        return filters;
    }


    getFiltersByGroup(groupName) {
        return this.groups[groupName] || [];
    }

    getFlatFiltersByGroup(groupName) {
        return this.getFiltersByGroup(groupName).flat();
    }

    clearGroup(groupName = null) {
        const name = this._getGroupName(groupName);
        delete this.groups[name];
        delete this.meta[name];
        return this;
    }

    clearFilterByField(field) {
        for (const name in this.groups) {
            this.groups[name] = this.groups[name]
                .map(group => group.filter(f => f.field !== field))
                .filter(group => group.length > 0);
        }
        return this;
    }

}


export default FilterService;
